import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import {
  CalendarIcon,
  ClockIcon,
  LocationMarkerIcon,
  UserIcon,
} from '@heroicons/react/outline';
import Skeleton from '../../../../components/Skeleton';
import MetaDataItem from '../../../../components/dialogs/EpisodeOfCareDetails/MetaDataItem';
import {
  getAppointmentStatusLabel,
  getMedicationStatusLabel,
  getProcedureStatusLabel,
  getRecordColor,
} from '../../../EpisodesOfCare/helpers';
import {
  MILESTONE_CLINICAL_EVENTS_TYPE__APPOINTMENTS,
  MILESTONE_CLINICAL_EVENTS_TYPE__MEDICATIONS,
  MILESTONE_CLINICAL_EVENTS_TYPE__PROCEDURES,
} from '../../../../common/constants';

const ClinicalEventDetails = ({ activity, clinicalEvent, loading }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  if (loading) {
    return (
      <div className="stack-4 p-6">
        <Skeleton className="h-10" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (!clinicalEvent) {
    return (
      <div className="p-6">
        <p className="text-gray-500">{t('noClinicalEventData')}</p>
      </div>
    );
  }

  const { domain, start, end, status, context = {} } = clinicalEvent;

  const renderAppointmentDetails = () => (
    <>
      <h2 className="text-lg font-normal">{t('details')}</h2>

      <div className="grid grid-cols-2 gap-4">
        <MetaDataItem label={t('type')} content={context.type} />
        <MetaDataItem
          label={t('forms:status.label')}
          content={
            <span
              className={`${getRecordColor(status)} text-xs px-2 py-1 rounded`}
            >
              {getAppointmentStatusLabel({ status, t })}
            </span>
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetaDataItem
          label={t('date')}
          content={toIntlDateTimeFormat(language, start)}
          icon={<CalendarIcon className="w-4 h-4" />}
        />
        <MetaDataItem
          label={t('time')}
          content={toIntlDateTimeFormat(language, start, {
            timeStyle: 'short',
          })}
          icon={<ClockIcon className="w-4 h-4" />}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetaDataItem
          label={t('duration')}
          content={`${(end - start) / 60000} minutes`}
        />
        <MetaDataItem
          label={t('location')}
          content={context.location}
          icon={<LocationMarkerIcon className="w-4 h-4" />}
        />
      </div>

      <hr className="border-divider" />

      <MetaDataItem
        label={t('forms:clinician.label')}
        content={context.clinicianName}
        icon={<UserIcon className="w-4 h-4" />}
      />
      {context.description && (
        <MetaDataItem
          label={t('forms:description.label')}
          content={context.description}
        />
      )}
      <MetaDataItem
        label={t('notes')}
        content={
          <div className="bg-neutral-400 p-2 rounded mt-1">{context.notes}</div>
        }
      />
      <MetaDataItem
        label={t('forms:identifier.label')}
        content={clinicalEvent.eventId}
      />
      <MetaDataItem
        label={t('type')}
        content={t('appointments', { count: 1 })}
      />
    </>
  );

  const renderMedicationDetails = () => (
    <>
      <h2 className="text-lg font-normal">{t('details')}</h2>

      <div className="grid grid-cols-2 gap-4">
        <MetaDataItem
          label={t('forms:status.label')}
          content={
            <span
              className={`${getRecordColor(status)} text-xs px-2 py-1 rounded`}
            >
              {getMedicationStatusLabel({ status, t })}
            </span>
          }
        />
        <MetaDataItem
          label={t('date')}
          content={toIntlDateTimeFormat(language, start)}
          icon={<CalendarIcon className="w-4 h-4" />}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetaDataItem label={t('forms:name.label')} content={context.name} />
        <MetaDataItem
          label={t('forms:dosage.label')}
          content={context.dosage}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetaDataItem label={t('forms:unit.label')} content={context.unit} />
        <MetaDataItem
          label={t('forms:frequency.label')}
          content={context.frequency}
        />
      </div>

      {context.route && (
        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            label={t('forms:route.label')}
            content={context.route}
          />
        </div>
      )}

      <hr className="border-divider" />

      <MetaDataItem
        label={t('forms:clinician.label')}
        content={context.prescriberName}
        icon={<UserIcon className="w-4 h-4" />}
      />
      {context.specialInstructions && (
        <MetaDataItem
          label={t('specialInstructions')}
          content={context.specialInstructions}
        />
      )}
      <MetaDataItem
        label={t('notes')}
        content={
          <div className="bg-neutral-400 p-2 rounded mt-1">{context.notes}</div>
        }
      />
      <MetaDataItem
        label={t('forms:identifier.label')}
        content={clinicalEvent.eventId}
      />
      <MetaDataItem
        label={t('type')}
        content={t('medications', { count: 1 })}
      />
    </>
  );

  const renderProcedureDetails = () => (
    <>
      <h2 className="text-lg font-normal">{t('procedureDetails')}</h2>

      <div className="grid grid-cols-2 gap-4">
        <MetaDataItem label={t('type')} content={context.type} />
        <MetaDataItem
          label={t('forms:status.label')}
          content={
            <span
              className={`${getRecordColor(status)} text-xs px-2 py-1 rounded`}
            >
              {getProcedureStatusLabel({ status, t })}
            </span>
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetaDataItem
          label={t('date')}
          content={toIntlDateTimeFormat(language, start)}
          icon={<CalendarIcon className="w-4 h-4" />}
        />
        <MetaDataItem
          label={t('time')}
          content={toIntlDateTimeFormat(language, start, {
            timeStyle: 'short',
          })}
          icon={<ClockIcon className="w-4 h-4" />}
        />
      </div>

      {end && start && (
        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            label={t('duration')}
            content={`${(end - start) / 60000} minutes`}
          />
          {context.location && (
            <MetaDataItem
              label={t('location')}
              content={context.location}
              icon={<LocationMarkerIcon className="w-4 h-4" />}
            />
          )}
        </div>
      )}

      <hr className="border-divider" />

      <MetaDataItem
        label={t('forms:clinician.label')}
        content={context.clinicianName}
        icon={<UserIcon className="w-4 h-4" />}
      />
      {context.description && (
        <MetaDataItem
          label={t('forms:description.label')}
          content={context.description}
        />
      )}
      <MetaDataItem
        label={t('notes')}
        content={
          <div className="bg-neutral-400 p-2 rounded mt-1">{context.notes}</div>
        }
      />

      {context.appointment && (
        <>
          <hr className="border-divider" />
          <h2 className="text-lg font-normal">
            {t('linkedAppointment', { defaultValue: 'Linked Appointment' })}
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <MetaDataItem
              label={t('appointmentDate', { defaultValue: 'Appointment Date' })}
              content={toIntlDateTimeFormat(
                language,
                context.appointment.appointmentStart,
              )}
              icon={<CalendarIcon className="w-4 h-4" />}
            />
            <MetaDataItem
              label={t('appointmentTime', { defaultValue: 'Appointment Time' })}
              content={`${toIntlDateTimeFormat(
                language,
                context.appointment.appointmentStart,
                {
                  timeStyle: 'short',
                },
              )} - ${toIntlDateTimeFormat(
                language,
                context.appointment.appointmentEnd,
                {
                  timeStyle: 'short',
                },
              )}`}
              icon={<ClockIcon className="w-4 h-4" />}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <MetaDataItem
              label={t('appointmentType', { defaultValue: 'Appointment Type' })}
              content={context.appointment.appointmentType || 'N/A'}
            />
            <MetaDataItem
              label={t('appointmentStatus', {
                defaultValue: 'Appointment Status',
              })}
              content={
                <span
                  className={`${getRecordColor(
                    context.appointment.appointmentStatus,
                  )} text-xs px-2 py-1 rounded`}
                >
                  {context.appointment.appointmentStatus}
                </span>
              }
            />
          </div>

          <MetaDataItem
            label={t('appointmentId', { defaultValue: 'Appointment ID' })}
            content={context.appointment.source_visit_occurrence_id}
          />
        </>
      )}

      <hr className="border-divider" />

      <MetaDataItem
        label={t('forms:identifier.label')}
        content={clinicalEvent.eventId}
      />
      <MetaDataItem label={t('type')} content={t('procedures', { count: 1 })} />
    </>
  );

  return (
    <div className="p-6">
      <div className="space-y-4">
        <h2 className="text-lg font-normal">{t('personalInfo')}</h2>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            label={t('forms:recipientId.label')}
            content={context.patientId}
          />
          <MetaDataItem
            label={t('forms:recipientName.label')}
            content={context.patientName}
          />
          <MetaDataItem
            label={t('forms:projectId.label')}
            content={context.projectId}
          />
          <MetaDataItem label={t('study')} content={context.study} />
        </div>

        <hr className="border-divider" />

        {activity.clinicalEventsType ===
          MILESTONE_CLINICAL_EVENTS_TYPE__APPOINTMENTS &&
          renderAppointmentDetails()}

        {activity.clinicalEventsType ===
          MILESTONE_CLINICAL_EVENTS_TYPE__MEDICATIONS &&
          renderMedicationDetails()}

        {activity.clinicalEventsType ===
          MILESTONE_CLINICAL_EVENTS_TYPE__PROCEDURES &&
          renderProcedureDetails()}
      </div>
    </div>
  );
};

ClinicalEventDetails.propTypes = {
  activity: PropTypes.shape({
    eventId: PropTypes.string,
    clinicalEventsType: PropTypes.string,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  clinicalEvent: PropTypes.object,
  loading: PropTypes.bool,
};

ClinicalEventDetails.defaultProps = {
  clinicalEvent: null,
  loading: false,
};

export default ClinicalEventDetails;
